import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { getPublicSurvey } from '../api/use-nps';
import type { NpsSurvey } from '@/shared/types/nps';
import { Skeleton } from '@/shared/ui/skeleton';
import { PublicSurveyLayout } from './public-survey-layout';
import { PublicSurveyUnavailable } from './public-survey-unavailable';
import { PublicSurveyWizard } from './public-survey-wizard';
import { PublicSurveySubmitCelebration } from './public-survey-submit-celebration';
import { PublicSurveyTopBar } from './public-survey-top-bar';
import { normalizePublicSurveyLanguage } from './i18n';

type Phase = 'wizard' | 'celebration';

export function PublicSurveyPage() {
  const { surveyId } = useParams<{ surveyId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const identifierFromUrl = searchParams.get('identifier') ?? '';
  const lang = normalizePublicSurveyLanguage(
    searchParams.get('lang') ?? i18n.language,
  );

  const [survey, setSurvey] = useState<NpsSurvey | null>(null);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState<Phase>('wizard');

  useEffect(() => {
    if (!surveyId) return;
    let cancelled = false;
    setLoading(true);
    getPublicSurvey(surveyId, lang)
      .then((s) => {
        if (!cancelled) setSurvey(s);
      })
      .catch(() => {
        if (!cancelled) setSurvey(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [surveyId, lang]);

  useEffect(() => {
    if (phase !== 'celebration') return;
    // `navigate(..., { replace: true })` troca a URL da pesquisa no histórico —
    // sem passar a própria URL como `returnTo`, o botão/redirect da tela de
    // confirmação caem no fallback `navigate(-1)`, que não tem mais a pesquisa
    // pra voltar (e em link direto de SMS/WhatsApp, geralmente não tem
    // histórico nenhum — o botão parece não fazer nada).
    const query = searchParams.toString();
    const returnTo = `/surveys/${surveyId}${query ? `?${query}` : ''}`;
    const t = window.setTimeout(
      () => navigate(`/surveys/confirm?returnTo=${encodeURIComponent(returnTo)}`, { replace: true }),
      1800,
    );
    return () => window.clearTimeout(t);
  }, [phase, navigate, surveyId, searchParams]);

  if (loading) {
    return (
      <div className="flex flex-1 flex-col bg-background">
        <PublicSurveyTopBar />
        <div className="px-4 py-10">
          <div className="container mx-auto max-w-2xl space-y-6">
            <Skeleton className="mx-auto h-40 w-full max-w-3xl rounded-xl" />
            <Skeleton className="mx-auto h-8 w-2/3" />
            <Skeleton className="h-48 w-full rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  const closed = !survey || ['FINISHED', 'PAUSED'].includes(survey.status);

  if (closed) {
    return <PublicSurveyUnavailable />;
  }

  return (
    <PublicSurveyLayout survey={survey}>
      <div className="mx-auto max-w-2xl">
        <AnimatePresence mode="wait">
          {phase === 'celebration' ? (
            <PublicSurveySubmitCelebration key="celebration" />
          ) : (
            <PublicSurveyWizard
              key="wizard"
              survey={survey}
              lang={lang}
              identifierFromUrl={identifierFromUrl}
              onSuccess={() => setPhase('celebration')}
            />
          )}
        </AnimatePresence>
      </div>
    </PublicSurveyLayout>
  );
}
